import { useState, useEffect, useContext, createContext } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from '../api/axios';

const PermissionsContext = createContext();

export const PermissionsProvider = ({ children }) => {
  const [permissions, setPermissions] = useState([]);
  const [permissionSlugs, setPermissionSlugs] = useState([]);
  const [dependencies, setDependencies] = useState({});
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (user?.id) {
      loadPermissions();
      loadDependencies();
    }
  }, [user?.id]);

  const loadPermissions = async () => {
    try {
      const response = await axios.get(`/api/rbac/users/${user.id}/permissions`);
      const perms = response.data.permissions || [];
      setPermissions(perms);
      setPermissionSlugs(perms.map(p => p.slug));
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const loadDependencies = async () => {
    try {
      const response = await axios.get('/api/rbac/dependencies');
      setDependencies(response.data);
    } catch (error) {
      // Set empty dependencies if loading fails
      setDependencies({});
    }
  };

  const hasPermission = (permission) => {
    if (!permissionSlugs.length) return false;
    if (permissionSlugs.includes('*')) return true;
    return permissionSlugs.includes(permission);
  };

  const hasAnyPermission = (permissionList) => {
    if (!permissionSlugs.length) return false;
    if (permissionSlugs.includes('*')) return true;
    return permissionList.some(perm => permissionSlugs.includes(perm));
  };

  const hasExplicitPermission = (permission) => {
    if (!permissions.length) return false;
    // Admin wildcard access
    if (permissions.some(p => p.slug === '*')) return true;
    const perm = permissions.find(p => p.slug === permission);
    return perm && perm.in_context_visible === true; // Explicit permissions have in_context_visible: true
  };

  const isAutoGranted = (permission) => {
    const perm = permissions.find(p => p.slug === permission);
    return perm && perm.in_context_visible;
  };

  const getVisibleModules = (moduleList) => {
    // Admin wildcard access - show all modules
    if (permissions.some(p => p.slug === '*')) {
      return moduleList;
    }
    
    return moduleList.filter(module => {
      const modulePermissions = Array.isArray(module.permission) ? module.permission : [module.permission];
      
      // Show module if user has any explicit (in_context_visible: true) permission
      const hasExplicit = modulePermissions.some(perm => {
        const permission = permissions.find(p => p.slug === perm);
        return permission && permission.in_context_visible === true;
      });
      
      return hasExplicit;
    });
  };

  const getModuleDependencies = (module) => {
    return dependencies[module] || [];
  };

  return (
    <PermissionsContext.Provider value={{
      permissions,
      permissionSlugs,
      dependencies,
      loading,
      hasPermission,
      hasAnyPermission,
      hasExplicitPermission,
      isAutoGranted,
      getModuleDependencies,
      getVisibleModules,
      refreshPermissions: loadPermissions
    }}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionsProvider');
  }
  return context;
};
